import { useState, useRef, useCallback, useEffect } from "react";
import { useRoute, useLocation } from "wouter";
import { useAuth } from "@clerk/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import AdminLayout from "./AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Save, Eye, EyeOff, ImagePlus, Loader2 } from "lucide-react";

interface EmailTemplate {
  id: number;
  name: string;
  subject: string;
  htmlBody: string;
  createdAt: string;
  updatedAt: string;
}

export default function AdminEmailTemplateEditPage() {
  const [matchEdit, paramsEdit] = useRoute("/admin/email-templates/:id/edit");
  const [, navigate] = useLocation();
  const { getToken } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();

  const isNew = !matchEdit;
  const templateId = matchEdit ? parseInt(paramsEdit?.id ?? "0", 10) : null;

  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [insertingImg, setInsertingImg] = useState(false);
  const [loaded, setLoaded] = useState(isNew);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const { isLoading } = useQuery<{ templates: EmailTemplate[] }>({
    queryKey: ["admin-email-templates"],
    enabled: !isNew,
    queryFn: async () => {
      const token = await getToken();
      const res = await fetch("/api/admin/email-templates", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? res.statusText);
      return res.json();
    },
    select: (data) => {
      if (templateId) {
        const found = data.templates.find(t => t.id === templateId);
        if (found && !loaded) {
          setName(found.name);
          setSubject(found.subject);
          setHtml(found.htmlBody);
          setLoaded(true);
        }
      }
      return data;
    },
  });

  async function uploadImageAndGetUrl(file: File): Promise<string> {
    const token = await getToken();
    const formData = new FormData();
    formData.append("image", file);
    const res = await fetch("/api/admin/upload-image", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error ?? "上傳失敗");
    return data.url as string;
  }

  function insertImgTag(url: string) {
    const ta = textareaRef.current;
    const imgTag = `<img src="${url}" alt="" style="max-width:100%;height:auto">`;
    if (ta) {
      const start = ta.selectionStart ?? html.length;
      const end = ta.selectionEnd ?? html.length;
      const newVal = html.slice(0, start) + imgTag + html.slice(end);
      setHtml(newVal);
      requestAnimationFrame(() => {
        ta.focus();
        ta.selectionStart = ta.selectionEnd = start + imgTag.length;
      });
    } else {
      setHtml(prev => prev + imgTag);
    }
  }

  const handlePaste = useCallback(async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = Array.from(e.clipboardData.items);
    const imgItem = items.find(item => item.type.startsWith("image/"));
    if (!imgItem) return;
    e.preventDefault();
    setInsertingImg(true);
    try {
      const file = imgItem.getAsFile();
      if (!file) return;
      const url = await uploadImageAndGetUrl(file);
      insertImgTag(url);
    } catch (err: any) {
      toast({ title: "圖片上傳失敗", description: err.message, variant: "destructive" });
    } finally {
      setInsertingImg(false);
    }
  }, [html, getToken]);

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setInsertingImg(true);
    try {
      const url = await uploadImageAndGetUrl(file);
      insertImgTag(url);
    } catch (err: any) {
      toast({ title: "圖片上傳失敗", description: err.message, variant: "destructive" });
    } finally {
      setInsertingImg(false);
      e.target.value = "";
    }
  }

  async function handleSave() {
    if (!name.trim()) { toast({ title: "請填寫範本名稱", variant: "destructive" }); return; }
    if (!subject.trim()) { toast({ title: "請填寫郵件主題", variant: "destructive" }); return; }
    if (!html.trim()) { toast({ title: "請填寫電郵內文", variant: "destructive" }); return; }

    setSaving(true);
    try {
      const token = await getToken();
      const url = isNew
        ? "/api/admin/email-templates"
        : `/api/admin/email-templates/${templateId}`;
      const method = isNew ? "POST" : "PUT";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: name.trim(), subject: subject.trim(), htmlBody: html.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "儲存失敗");
      toast({ title: isNew ? "範本已建立" : "範本已更新", description: `「${name.trim()}」已成功儲存` });
      qc.invalidateQueries({ queryKey: ["admin-email-templates"] });
      navigate("/admin/email-templates");
    } catch (e: any) {
      toast({ title: "儲存失敗", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminLayout tab="email-templates">
      <div className="max-w-3xl space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin/email-templates")}>
            <ArrowLeft className="h-4 w-4 mr-1" /> 返回列表
          </Button>
          <div>
            <h2 className="text-lg font-semibold">{isNew ? "新增電郵範本" : "編輯電郵範本"}</h2>
            <p className="text-xs text-muted-foreground">圖片上載至伺服器並以 HTTPS 連結顯示，相容 Gmail</p>
          </div>
        </div>

        {!isNew && isLoading && !loaded ? (
          <div className="space-y-3">
            <Skeleton className="h-10 rounded" />
            <Skeleton className="h-10 rounded" />
            <Skeleton className="h-64 rounded" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="tpl-name">範本名稱 <span className="text-destructive">*</span></Label>
              <Input
                id="tpl-name"
                placeholder="例：每月促銷電郵、週年通知…"
                value={name}
                onChange={e => setName(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="tpl-subject">郵件主題 <span className="text-destructive">*</span></Label>
              <Input
                id="tpl-subject"
                placeholder="例：[Skoltend] 今日最新招標通知"
                value={subject}
                onChange={e => setSubject(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="tpl-html">電郵 HTML 內文 <span className="text-destructive">*</span></Label>
                <div className="flex gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5 h-7 text-xs"
                    disabled={insertingImg}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {insertingImg
                      ? <Loader2 className="h-3 w-3 animate-spin" />
                      : <ImagePlus className="h-3 w-3" />}
                    {insertingImg ? "上傳中…" : "插入圖片"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5 h-7 text-xs"
                    onClick={() => setShowPreview(p => !p)}
                  >
                    {showPreview ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                    {showPreview ? "隱藏預覽" : "顯示預覽"}
                  </Button>
                </div>
              </div>
              <Textarea
                id="tpl-html"
                ref={textareaRef}
                className="font-mono text-xs min-h-[400px] resize-y"
                placeholder="在此貼入 HTML 電郵內文，可直接 Ctrl+V 貼上圖片（自動上傳至伺服器）"
                value={html}
                onChange={e => setHtml(e.target.value)}
                onPaste={handlePaste}
              />
              <p className="text-xs text-muted-foreground">
                支援直接貼上圖片（Ctrl+V）或點擊「插入圖片」上傳——圖片會自動儲存至伺服器並以 HTTPS URL 嵌入，解決 Gmail 無法顯示圖片的問題。
              </p>
            </div>

            {showPreview && html && (
              <div className="space-y-1.5">
                <Label>預覽</Label>
                <div className="border rounded-lg overflow-hidden">
                  <iframe
                    srcDoc={html}
                    className="w-full min-h-[500px] bg-white"
                    title="電郵預覽"
                    sandbox="allow-same-origin"
                  />
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button onClick={handleSave} disabled={saving} className="gap-1.5">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {saving ? "儲存中…" : "儲存範本"}
              </Button>
              <Button variant="outline" onClick={() => navigate("/admin/email-templates")}>
                取消
              </Button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
